import truckload from './assets/truckload.avif'
import cloudLogo from './assets/GntB5.jpg'
import TransportIcon from './components/icons/TransportIcon'
import InvestmentIcon from './components/icons/InvestmentIcon'
import TechnologyIcon from './components/icons/TechnologyIcon'

const contentEn = {
    brand: 'Groupe Ouambo',
    tagline: 'Technology & Digitalization Holding',
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      technology: 'Technology & AI',
      platforms: 'Digital platforms',
      energy: 'Energy',
      transport: 'Logistics & corridor',
      investment: 'Investment / partnerships',
      contact: 'Contact',
      careers: 'Careers',
      cta: 'Start a project'
    },
    hero: {
      eyebrow: 'Technology & Digitalization',
      title: 'Technology and digitalization',
      subtitle:
        'AI-enabled platforms for learning, commerce, and operations — built for local realities, delivered on the ground.',
      primary: 'Start a project',
      secondary: 'Meet the team'
    },
    stats: [
      { value: '2023', label: 'Holding founded' },
      { value: '3', label: 'Flagship platforms' },
      { value: 'CM→CAR', label: 'On-ground delivery' }
    ],
    highlights: [
      { label: 'OnboardLearning.org — EdTech / bilingual LMS', href: 'https://onboardlearning.org' },
      { label: 'Galababa.com — Bangui marketplace', href: 'https://galababa.com' },
      { label: 'KesseDesk — live chat & domains for businesses', href: 'https://www.kessedesk.com' },
      { label: 'Practical AI & institutional digitalization' }
    ],
    about: {
      title: 'Who We Are',
      lead:
        'Groupe Ouambo is a technology and digitalization group. Founded in 2023 as a strategic investment and technology holding, we build and operate digital platforms — including OnboardLearning, Galababa and KesseDesk — support institutions adopting modern tools and practical AI, and maintain a corridor logistics network so those products reach real users on the ground.',
      readMore: 'Read More',
      paragraphs: [
        'We create long-term value by shipping products people use — learning platforms, marketplaces, and digitalization programs — backed by on-ground delivery from Cameroon to CAR.',
        'Logistics and corridor operations remain an operational pillar that enables trade and last-mile reach, so digital products and commerce can move across the region.',
        'In parallel, we invest in technology that improves productivity, modernizes processes, and accelerates responsible digital transformation.',
        'Groupe Ouambo operates with disciplined investment, strong governance, and a long-term vision.',
        'We partner with public institutions, private enterprises, and international stakeholders for sustainable growth and shared value.'
      ],
      vision: {
        title: 'Our Vision',
        body:
          'To be the trusted technology and digitalization group — recognized for platforms that work on the ground and partnerships that last.'
      },
      values: {
        title: 'Our Values',
        items: [
          { title: 'Integrity', body: 'Transparency and ethical governance in all our operations' },
          { title: 'Innovation', body: 'Practical AI and digital tools that fit local realities' },
          { title: 'Excellence', body: 'Commitment to quality, performance, and continuous improvement' },
          { title: 'Partnership', body: 'Building long-term, value-driven relationships' },
          { title: 'Sustainability', body: 'Investing with a long-term economic and social impact perspective' }
        ]
      },
      cards: [
        {
          title: 'Technology & AI',
          body: 'Platforms and practical AI for learning, commerce, and operations.'
        },
        {
          title: 'Digital platforms',
          body: 'OnboardLearning, Galababa and KesseDesk — our flagship products.'
        },
        {
          title: 'Logistics backbone',
          body: 'Corridor network that helps digital products reach real users.'
        }
      ]
    },
    businesses: {
      title: 'Our Pillars',
      intro:
        'Technology and digitalization lead. Logistics remains a backbone. Investment fuels partnerships.',
      readMore: 'Read More',
      items: [
        {
          title: 'Technology & AI',
          body:
            'Practical digitalization and responsible AI — platforms, tools, and services for institutions and businesses.',
          iconSvg: <TechnologyIcon />,
          iconAlt: 'Technology and AI',
          route: '/technology'
        },
        {
          title: 'Digital platforms',
          body:
            'Flagship products: OnboardLearning.org (bilingual LMS), Galababa.com (Bangui marketplace) and KesseDesk — live chat and domains for businesses: add customer support to your website in minutes.',
          iconEmoji: '💻',
          iconAlt: 'Digital platforms',
          route: '/technology#platforms',
          products: [
            { label: 'OnboardLearning.org', href: 'https://onboardlearning.org' },
            { label: 'Galababa.com', href: 'https://galababa.com' },
            { label: 'KesseDesk', href: 'https://www.kessedesk.com' }
          ]
        },
        {
          title: 'Energy',
          body:
            'We are exploring partnerships around energy access to support digitalization, operations, and communities — through studies, partners, and progressive rollout.',
          iconEmoji: '⚡',
          iconAlt: 'Energy',
          route: '/technology#energy'
        },
        {
          title: 'Logistics & corridor',
          body:
            'Physical corridor network and transit operations — the operational backbone that helps digital products and trade reach real users from Cameroon to CAR.',
          iconSvg: <TransportIcon />,
          iconAlt: 'Logistics and corridor',
          route: '/transport'
        },
        {
          title: 'Investment / partnerships',
          body: 'Strategic capital and partnerships that strengthen digitalization, infrastructure, and regional growth.',
          iconSvg: <InvestmentIcon />,
          iconAlt: 'Investment and partnerships',
          route: '/investment'
        }
      ]
    },
    pages: {
      transport: {
        title: 'Logistics & Corridor',
        lead:
          'We operate logistics and corridor capabilities that connect regional trade — the backbone that supports digital delivery on the ground.',
        image: truckload,
        imageAlt: 'Corridor logistics',
        cta: 'Start to ship',
        ctaSecondary: 'Contact for border custom',
        points: [
          'Transit operations and corridor logistics',
          'Import-export facilitation and compliance',
          'Supply chain visibility and efficiency tools'
        ],
        longSections: []
      },
      investment: {
        title: 'Investment & Partnerships',
        lead:
          'We deploy capital and build partnerships where they create long-term digital and economic value.',
        points: [
          'Digitalization and platform partnerships',
          'Infrastructure and regional trade enablement',
          'Governance-first investment structures'
        ]
      },
      technology: {
        title: 'Technology & AI',
        lead:
          'Practical digitalization and responsible AI — platforms for learning, commerce, and operations.',
        image: cloudLogo,
        imageAlt: 'Technology platforms',
        cta: 'Start a new project',
        ctaRoute: '/contact',
        points: [
          'AI-enabled platforms with human oversight',
          'Digital platforms for modern economies',
          'On-ground delivery from Cameroon to CAR'
        ],
        longSections: [
          {
            title: 'Digital Platforms',
            body:
              'OnboardLearning.org — bilingual LMS for education and skills. Galababa.com — Bangui marketplace for local commerce. KesseDesk — live chat and domains for businesses: add customer support to your website in minutes. Built for local realities.'
          },
          {
            title: 'IT Equipment and Deployment',
            body:
              'We deploy IT equipment tailored to modern operational needs - servers, networking devices, computing hardware, and supporting infrastructure.'
          },
          {
            title: 'Connectivity and Digital Ecosystems',
            body:
              'We facilitate internet connectivity through high-performance networks, last-mile solutions, and digital communication systems — helping organizations build resilient digital ecosystems.'
          }
        ]
      }
    },
    equipment: {
      title: 'Equipment',
      lead: 'Reliable IT equipment tailored for modern operations and growth.',
      linkLabel: 'View details',
      backLabel: 'Back to equipment',
      notFound: 'This equipment item is not available.',
      items: [
        {
          slug: 'cloud-server-bundle',
          name: 'Cloud Server Bundle',
          price: '$2,500',
          description: 'High-performance compute for cloud deployments and enterprise workloads.'
        },
        {
          slug: 'enterprise-router',
          name: 'Enterprise Router',
          price: '$1,200',
          description: 'Secure, scalable routing for multi-site connectivity.'
        },
        {
          slug: 'data-storage-array',
          name: 'Data Storage Array',
          price: '$3,800',
          description: 'Resilient storage for critical business data and backups.'
        },
        {
          slug: 'network-switch-48-port',
          name: 'Network Switch 48-Port',
          price: '$950',
          description: 'High-throughput switching for campus and data center environments.'
        },
        {
          slug: 'security-firewall-appliance',
          name: 'Security Firewall Appliance',
          price: '$1,600',
          description: 'Advanced protection with monitoring and policy control.'
        }
      ]
    },
    contact: {
      title: 'Contact',
      lead: 'Tell us about your goals. We reply within 48 hours.',
      form: {
        name: 'Name',
        email: 'Email',
        message: 'Message',
        submit: 'Send message',
        heading: 'Contact Us',
        success: 'Message sent. We will get back to you soon.',
        error: 'Something went wrong. Please try again.'
      },
      details: [
        { title: 'Adresse', body: 'USA 7502 Diplomat Dr ste 101, Unit B, Manassas VA 20109' },
        { title: 'Siege social', body: 'Place de la Republique' },
        { title: 'Telephone', body: '+237674687668 / +23672795479' },
        { title: 'Zone Afrique', body: 'Central African Republic | Email: mp@groupeouambo.com' },
        { title: 'Cameroun', body: 'Yaounde | Tel: +23776083761 | Email: dg@groupeouambo.com' },
        { title: 'Zone USA', body: 'Rodrigue OUAMBO' },
        { title: 'Contact USA', body: '7502 Diplomat Dr, Manassas VA 20109 | Tel: +17038641447 | Email: rod@groupeouambo.com' },
        { title: 'Zone Europe', body: 'Villeurbanne' },
        { title: 'Contact Europe', body: 'Tel: +33651677745 | Email: info@groupeouambo.com' }
      ]
    },
    careers: {
      heroEyebrow: 'Join Our Team',
      heroTitle: 'Careers at Groupe Ouambo',
      heroSub: 'We are building the next generation of digital platforms and technology. Come grow with us.',
      openPositions: 'Open Positions',
      applyNow: 'Apply Now',
      myApplications: 'My Applications',
      signInRegister: 'Sign In / Register',
      loadingPositions: 'Loading positions...',
      noPositionsTitle: 'No open positions right now',
      noPositionsBody: 'Check back soon or send a general application.',
      sendGeneral: 'Send General Application',
      signInToApply: 'Sign In to Apply',
      description: 'Description',
      requirements: 'Requirements',
      applyForPosition: 'Apply for this Position',
      generalApplication: 'General Application',
      applyPrefix: 'Apply -',
      needAccount: 'You need an account to apply. Sign in or create a free account to submit your application and track its status.',
      signInCreate: 'Sign In / Create Account',
      applicationSubmitted: 'Application submitted! A confirmation has been sent to',
      willBeInTouch: 'We will be in touch soon.',
      applyingAs: 'Applying as',
      selectPosition: 'Select a Position (optional)',
      generalSpontaneous: 'General / Spontaneous Application',
      coverLetter: 'Cover Letter',
      optional: '(optional)',
      coverPlaceholder: 'Tell us why you want to join Groupe Ouambo...',
      resume: 'Resume / CV',
      resumeHint: '(PDF, DOC, or DOCX - optional)',
      submitting: 'Submitting...',
      submitApplication: 'Submit Application',
      clear: 'Clear',
      applicantAccount: 'Applicant Account',
      authSub: 'Sign in or create a free account to apply and track your application status.',
      signIn: 'Sign In',
      createAccount: 'Create Account',
      fullName: 'Full Name',
      namePlaceholder: 'Your name',
      emailAddress: 'Email Address',
      password: 'Password',
      pleaseWait: 'Please wait...',
      signOut: 'Sign Out',
      submitNew: '+ Submit New Application',
      loading: 'Loading...',
      noAppsTitle: 'No applications yet',
      noAppsBody: 'Browse open positions and submit your first application.',
      applied: 'Applied',
      noteFromHr: 'Note from HR',
      pleaseSignInApps: 'Please sign in to view your applications.',
      statusPending: 'Pending',
      statusReviewing: 'Reviewing',
      statusAccepted: 'Accepted',
      statusRejected: 'Rejected'
    },
    footer: 'Groupe Ouambo'
  }

export default contentEn
